import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToNotifications,
  subscribeToCompanyUnreadCount,
  fetchOlderNotifications,
  fetchVisibleUnreadCount,
  markNotificationRead,
  markAllVisibleNotificationsRead,
  deleteNotification as deleteNotificationDoc,
  isNotificationVisible,
  isIncomingTeamInvitation,
  mergeNotifications,
  formatNotificationBadgeCount,
} from '@/services/notifications';
import type { Notification } from '@/types';

export interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  unreadBadgeLabel: string;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

export function useNotifications(_autoRefresh = true): UseNotificationsReturn {
  const { firebaseUser, selectedCompany } = useAuth();
  const uid = firebaseUser?.uid;
  const companyId = selectedCompany?.id ?? undefined;

  const [liveNotifications, setLiveNotifications] = useState<Notification[]>([]);
  const [olderNotifications, setOlderNotifications] = useState<Notification[]>([]);
  const [liveHasMore, setLiveHasMore] = useState(false);
  const [olderHasMore, setOlderHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMoreUnread, setHasMoreUnread] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    if (!uid) {
      setUnreadCount(0);
      setHasMoreUnread(false);
      return;
    }
    try {
      const { count, hasMore } = await fetchVisibleUnreadCount(uid, companyId);
      setUnreadCount(count);
      setHasMoreUnread(hasMore);
    } catch (err) {
      console.warn('[useNotifications:unreadCount]', (err as Error).message);
    }
  }, [uid, companyId]);

  useEffect(() => {
    if (!uid) {
      setLiveNotifications([]);
      setOlderNotifications([]);
      setLiveHasMore(false);
      setOlderHasMore(false);
      setUnreadCount(0);
      setHasMoreUnread(false);
      return;
    }

    setOlderNotifications([]);
    setOlderHasMore(false);

    let companyUnread = 0;
    let companyUnreadHasMore = false;
    let invitationUnread = 0;

    const publishUnreadTotal = () => {
      setUnreadCount(companyUnread + invitationUnread);
      setHasMoreUnread(companyUnreadHasMore || invitationUnread > 0);
    };

    const unsubCompany = companyId
      ? subscribeToCompanyUnreadCount(
          uid,
          companyId,
          (count, hasMore) => {
            companyUnread = count;
            companyUnreadHasMore = hasMore;
            publishUnreadTotal();
          },
          (err) => {
            console.warn('[useNotifications:companyUnread]', err.message);
          },
        )
      : () => undefined;

    const unsubFeed = subscribeToNotifications(
      uid,
      companyId,
      ({ notifications, hasMore }) => {
        setLiveNotifications(notifications);
        setLiveHasMore(hasMore);
        invitationUnread = notifications.filter(
          (n) => isIncomingTeamInvitation(n) && !n.read,
        ).length;
        publishUnreadTotal();
      },
      (err) => {
        console.warn('[useNotifications]', err.message);
      },
    );

    // Badge count comes from live listeners above — avoid refreshUnreadCount() here
    // because getCountFromServer can finish after a snapshot and overwrite a higher count.

    return () => {
      unsubCompany();
      unsubFeed();
    };
  }, [uid, companyId]);

  const allNotifications = useMemo(
    () => mergeNotifications(liveNotifications, olderNotifications),
    [liveNotifications, olderNotifications],
  );

  const notifications = useMemo(
    () => allNotifications.filter((notification) => isNotificationVisible(notification, companyId)),
    [allNotifications, companyId],
  );

  const unreadBadgeLabel = formatNotificationBadgeCount(unreadCount, hasMoreUnread);
  const hasMore = olderHasMore || (olderNotifications.length === 0 && liveHasMore);

  const loadMore = useCallback(async () => {
    if (!uid || !companyId || loadingMore || !hasMore) return;

    const companyNotifs = notifications.filter((n) => n.companyId === companyId);
    const oldestCompany = companyNotifs[companyNotifs.length - 1];
    if (!oldestCompany) return;

    setLoadingMore(true);
    try {
      const { notifications: page, hasMore: pageHasMore } = await fetchOlderNotifications(
        uid,
        companyId,
        oldestCompany.createdAt,
      );
      setOlderNotifications((prev) => mergeNotifications(prev, page));
      setOlderHasMore(pageHasMore);
    } catch (err) {
      console.warn('[useNotifications:loadMore]', (err as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [uid, companyId, loadingMore, hasMore, notifications]);

  const refresh = useCallback(async () => {
    await refreshUnreadCount();
  }, [refreshUnreadCount]);

  const markRead = useCallback(
    async (id: string) => {
      if (!uid) return;
      await markNotificationRead(uid, id);
      await refreshUnreadCount();
    },
    [uid, refreshUnreadCount],
  );

  const markAllRead = useCallback(async () => {
    if (!uid) return;
    const feedUnreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    await markAllVisibleNotificationsRead(uid, companyId, feedUnreadIds);
    await refreshUnreadCount();
  }, [uid, companyId, notifications, refreshUnreadCount]);

  const deleteNotification = useCallback(
    async (id: string) => {
      if (!uid) return;
      await deleteNotificationDoc(uid, id);
      setLiveNotifications((prev) => prev.filter((n) => n.id !== id));
      setOlderNotifications((prev) => prev.filter((n) => n.id !== id));
      await refreshUnreadCount();
    },
    [uid, refreshUnreadCount],
  );

  return {
    notifications,
    unreadCount,
    unreadBadgeLabel,
    hasMore,
    loadingMore,
    loadMore,
    refresh,
    markRead,
    markAllRead,
    deleteNotification,
  };
}
